/* eslint-disable react/prop-types */
import React from 'react';
import { Card, CardContent, Typography, Box, Chip } from '@mui/material';
import { ICalendarSaleOrder } from '../../types';
import routes from 'constants/routes';

interface SalesOrderCalendarCardProps {
	order: ICalendarSaleOrder;
	showBillableCustomer?: boolean;
}

export const SalesOrderCalendarCard: React.FC<SalesOrderCalendarCardProps> = ({
	order,
	showBillableCustomer = false,
}) => {
	const handleClick = () => {
		const url = routes.salesOrder.edit.replace(':id', order.saleOrderId);
		window.open(url, '_blank');
	};

	// Función para convertir texto a Title Case
	const toTitleCase = (str: string | undefined): string => {
		if (!str) return '';
		return str.toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase());
	};

	const truncateText = (text: string | string[] | undefined, maxLength: number): string => {
		if (!text) return '';
		if (Array.isArray(text)) {
			const joined = text.join(', ');
			return joined.length > maxLength ? `${joined.substring(0, maxLength)}...` : joined;
		}
		return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
	};

	// Mejorar la lógica para detectar saldo pendiente
	const hasPendingPayment = order.pendingPayAmount != null && Number(order.pendingPayAmount) > 0;

	// Función para obtener el nombre a mostrar según el toggle
	const getDisplayName = (): string => {
		if (showBillableCustomer) {
			// Mostrar cliente facturable
			if (order.customersCompleteName && order.customersCompleteName.length > 0) {
				return order.customersCompleteName.join(', ');
			}
			return '-';
		} else {
			// Mostrar pasajero principal
			return order.mainPassengerCompleteName || '-';
		}
	};

	return (
		<Card
			elevation={0}
			sx={{
				cursor: 'pointer',
				transition: 'all 0.15s ease-in-out',
				border: '1px solid',
				borderColor: hasPendingPayment ? 'warning.main' : 'grey.300',
				borderRadius: 1,
				bgcolor: hasPendingPayment ? 'warning.50' : 'white',
				'&:hover': {
					borderColor: 'primary.main',
					bgcolor: hasPendingPayment ? 'warning.100' : 'primary.50',
					transform: 'translateY(-1px)',
					boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
				},
				mb: 0,
				overflow: 'hidden',
				minHeight: 36, // Altura más compacta
				maxHeight: 44, // Altura máxima reducida
				flexShrink: 0,
			}}
			onClick={handleClick}
		>
			<CardContent sx={{ p: 0.5, '&:last-child': { pb: 0.5 } }}>
				{/* Header con nombre y indicador de estado */}
				<Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.25 }}>
					<Typography
						variant="body2"
						sx={{
							fontWeight: 600,
							lineHeight: 1.1,
							fontSize: '0.675rem',
							color: 'text.primary',
							flex: 1,
							pr: 0.5,
						}}
					>
						{toTitleCase(truncateText(getDisplayName(), 18))}
					</Typography>

					{/* Indicador de estado de pago */}
					<Box
						sx={{
							width: 4,
							height: 4,
							borderRadius: '50%',
							bgcolor: hasPendingPayment ? 'error.main' : 'success.main',
							flexShrink: 0,
							mt: 0.25,
						}}
					/>
				</Box>

				{/* Deal ID y saldo en línea */}
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
					<Typography
						variant="caption"
						sx={{
							fontSize: '0.575rem',
							color: 'text.secondary',
							fontWeight: 500,
							lineHeight: 1,
						}}
					>
						Deal #{order.dealId || '---'}
					</Typography>

					{/* Saldo pendiente - solo si realmente hay saldo pendiente */}
					{hasPendingPayment && (
						<Chip
							label={`$${Number(order.pendingPayAmount).toFixed(0)}`}
							size="small"
							sx={{
								height: 14,
								fontSize: '0.5rem',
								fontWeight: 600,
								bgcolor: 'error.main',
								color: 'white',
								'& .MuiChip-label': {
									px: 0.375,
									py: 0,
								},
							}}
						/>
					)}
				</Box>
			</CardContent>
		</Card>
	);
};
